import { useEffect } from 'react'
import { supabase } from '@/config/supabase'
import { useAuthStore } from '@/stores/auth.store'
import type { User } from '@/types/database'

export function useAuth() {
    const { session, user, tenant, isLoading, isAuthenticated, setSession, setUser, setTenant, setLoading, logout } =
        useAuthStore()

    useEffect(() => {
        // Get initial session
        supabase.auth.getSession().then(({ data: { session: s } }) => {
            setSession(s)
            if (s) {
                loadUserProfile(s.user.id)
            } else {
                setLoading(false)
            }
        })

        // Listen for auth changes
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, s) => {
            setSession(s)
            if (s) {
                loadUserProfile(s.user.id)
            } else {
                logout()
                setLoading(false)
            }
        })

        return () => subscription.unsubscribe()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    async function loadUserProfile(authId: string) {
        try {
            const { data: userData, error } = await supabase
                .from('veic_users')
                .select('*, tenant:veic_tenants(*)')
                .eq('auth_id', authId)
                .eq('ativo', true)
                .is('deleted_at', null)
                .single()

            if (error || !userData) {
                console.error('Error loading user profile:', error)
                setLoading(false)
                return
            }

            const userProfile = userData as unknown as User & { tenant: typeof tenant }
            setUser(userProfile)
            setTenant(userProfile.tenant)
        } catch (err) {
            console.error('Error loading user profile:', err)
        } finally {
            setLoading(false)
        }
    }

    async function signIn(email: string, password: string) {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
    }

    async function signOut() {
        await supabase.auth.signOut()
        logout()
    }

    return {
        session,
        user,
        tenant,
        isLoading,
        isAuthenticated,
        signIn,
        signOut,
    }
}
